import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment-timezone";
import { getAppointmentSlots, getTimeZoneList } from "./ApiService";
import { CircularProgress } from "@mui/material";

/**
 * ---- Interfaces (align with Angular structure) ----
 */
interface TimeZoneItem {
  id: string;
  displayName: string;
  currentTimeZone?: boolean;
}

interface ShiftSlot {
  id: string;
  date: string; // ISO date string (e.g., "2025-08-13")
  startTime: string; // "HH:mm"
  endTime: string; // "HH:mm"
}

interface BookingShiftwiseItem {
  date: string;
  morningSlots: ShiftSlot[];
  afternoonSlots: ShiftSlot[];
  eveningSlots: ShiftSlot[];
  nightSlots: ShiftSlot[];
}

interface SelectedRCAppSlots {
  selectedTimeZone?: string; // tz id
  selectedMoment?: { year: number; month: number; monthName: string };
  selectedDate?: string; // ISO
  selectedSlot?: ShiftSlot;
  bookingSlotsDatewise?: Record<string, ShiftSlot[]>;
}

interface Props {
  recruiterDetails?: any;
  formHeaderDetails?: any;
  /** optional dataLayer hook (defaults to window.dataLayer) */
  dataLayerPush?: (event: Record<string, any>) => void;
}

/**
 * ---- Helpers mimicking Angular StandaloneService + DataLayer ----
 */
const STORAGE_KEY = "selectedRCAppSlots";

function readPersisted(): SelectedRCAppSlots {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SelectedRCAppSlots) : {};
  } catch {
    return {};
  }
}

function writePersisted(p: SelectedRCAppSlots) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch { }
}

function pushDL(
  fn: Props["dataLayerPush"] | undefined,
  payload: Record<string, any>
) {
  if (fn) return fn(payload);
  // fallback to window.dataLayer if available
  // @ts-ignore
  if (typeof window !== "undefined" && window.dataLayer) {
    // @ts-ignore
    window.dataLayer.push(payload);
  }
}

/**
 * ---- Component ----
 */
const ScheduleCall: React.FC<Props> = ({
  recruiterDetails,
  formHeaderDetails,
  dataLayerPush,
}) => {
  // Angular: selectedMoment = {year, month: 1..12, monthName}
  const initialMoment = useMemo(
    () => ({
      year: moment().year(),
      month: moment().month() + 1,
      monthName: moment().format("MMMM"),
    }),
    []
  );

  const persisted = useMemo(readPersisted, []);
  const [selectedMoment, setSelectedMoment] = useState<{
    year: number;
    month: number; // 1..12
    monthName: string;
  }>(persisted.selectedMoment || initialMoment);

  const [availableTimeZonesList, setAvailableTimeZonesList] = useState<
    TimeZoneItem[]
  >([]);
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>(
    persisted.selectedTimeZone || ""
  );

  const [appointmentSlotsData, setAppointmentSlotsData] = useState<any>({});
  const [bookingSlotsDatewise, setBookingSlotsDatewise] = useState<
    Record<string, ShiftSlot[]>
  >(persisted.bookingSlotsDatewise || {});
  const [bookingSlotsShiftWise, setBookingSlotsShiftwise] = useState<any>();
  const [calendarData, setCalendarData] = useState<any>([]);

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  
  // Memoize start and end dates to avoid unnecessary recalculations
  const { startDate, endDate } = useMemo(() => {
    const start = moment()
      .year(selectedMoment.year)
      .month(selectedMoment.month - 1) // month is 0-indexed in moment
      .startOf("month")
      .format("DDMMYYYY");

    const end = moment()
      .year(selectedMoment.year)
      .month(selectedMoment.month - 1)
      .endOf("month")
      .format("DDMMYYYY");

    return { startDate: start, endDate: end };
  }, [selectedMoment.year, selectedMoment.month]);


  // Angular day header order (Monday..Sunday) - memoized
  const nameOfDays = useMemo(() => [
    { name: "Monday", code: "Lbl_Mon" },
    { name: "Tuesday", code: "Lbl_Tue" },
    { name: "Wednesday", code: "Lbl_Wed" },
    { name: "Thursday", code: "Lbl_Thu" },
    { name: "Friday", code: "Lbl_Fri" },
    { name: "Saturday", code: "Lbl_Sat" },
    { name: "Sunday", code: "Lbl_Sun" },
  ], []);

  // scroll target like Angular's "book-appointment"
  const containerRef = useRef<HTMLDivElement>(null);
  
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, []);

  // Memoize calendar colors
  const calendarColors = useMemo(() => ({
    main: formHeaderDetails?.calendarMainColor || "#2959AF",
    hover: formHeaderDetails?.calendarHoverColor || "#1E3D7A",
  }), [formHeaderDetails?.calendarMainColor, formHeaderDetails?.calendarHoverColor]);

  // Helper function to format time (remove seconds if present)
  const formatTime = useCallback((time: string) => {
    if (!time) return "";
    // If time is in format "HH:mm:ss", remove seconds
    if (time.includes(":")) {
      const parts = time.split(":");
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return time;
  }, []);

  /**
   * ---- Effects: init & TimeZones ----
   */
  useEffect(() => {
    // dataLayer: jr_recruiterwidget_view_calendar
    const recName = `${recruiterDetails.firstName || ""} ${recruiterDetails.lastName || ""
      }`.trim();
    pushDL(dataLayerPush, {
      event: "jr_recruiterwidget_view_calendar",
      userId: recruiterDetails.userId,
      recName,
      atsId: recruiterDetails.atsId,
      vacancyTitle: recruiterDetails.vacancytitle,
      officeId: recruiterDetails.officedId,
    });

    // fetch timezones then slots
    (async () => {
      try {
        const tz = await getTimeZoneList();
        const list = Array.isArray(tz) ? tz : [];
        setAvailableTimeZonesList(list);

        let tzId = selectedTimeZone;
        if (!tzId) {
          const current = list.find((t: any) => t.currentTimeZone);
          tzId = current?.id || list[0]?.id || "";
          setSelectedTimeZone(tzId);
        }
      } catch (e) {
        console.error("getTimeZoneList error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ---- Fetch appointment slots on month/tz change ----
   */
  useEffect(() => {
    getAppointmentSlotsList();
  }, [selectedTimeZone, selectedMoment.month]);

  const getAppointmentSlotsList = useCallback(async () => {
    const payload = {
      clientCode: recruiterDetails?.clientCode || "JRTestL",
      appointmentId: formHeaderDetails?.appointmentId || 1873,
      calendarId: formHeaderDetails?.calendarId || [2242],
      userTimeZoneId: selectedTimeZone,
      sourceOwnerId: recruiterDetails?.userId || 11635,
      calendarOption: formHeaderDetails?.calendarOption || "PRE_DEFINED",
      startDate,
      endDate,
    };
    
    setIsLoader(true);
    
    try {
      const result: any = await getAppointmentSlots(payload);
      
      // Simulate loading delay
      setTimeout(() => setIsLoader(false), 1000);
      
      const _bookingSlotsDatewise: Record<string, ShiftSlot[]> = {};

      if (result?.data?.bookingSlotsShiftwise?.length > 0) {
        result.data.bookingSlotsShiftwise.forEach((item: BookingShiftwiseItem) => {
          const tempDate = moment(item.date).format("YYYY-MM-DD");
          const tempMonth = moment(item.date).format("YYYY-M");

          // ✅ Check if this date belongs to the currently selected month
          if (
            tempMonth === `${selectedMoment.year}-${selectedMoment.month}`
          ) {
            _bookingSlotsDatewise[tempDate] = [
              ...(item.morningSlots || []),
              ...(item.afternoonSlots || []),
              ...(item.eveningSlots || []),
              ...(item.nightSlots || []),
            ];
          }
        });
      }

      const slots = result?.data?.bookingSlotsShiftwise || [];

      setAppointmentSlotsData(result?.data || {});
      setBookingSlotsDatewise(_bookingSlotsDatewise);
      setBookingSlotsShiftwise(slots);
      setSelectedSlot(null);
      setSelectedDate(null);
      setCalendarData([]);

      // Build calendar for the correct month
      const slotDates =
        slots.map((slot: any) => moment(slot.date).format("YYYY-MM-DD")) || [];
      createCalendar(selectedMoment.year, selectedMoment.month, slotDates);

      const toPersist: SelectedRCAppSlots = {
        ...readPersisted(),
        selectedTimeZone,
        bookingSlotsDatewise: _bookingSlotsDatewise,
        selectedMoment,
      };
      writePersisted(toPersist);
    } catch (error) {
      console.error("Error fetching appointment slots:", error);
      setIsLoader(false);
    }
  }, [recruiterDetails, formHeaderDetails, selectedTimeZone, selectedMoment, startDate, endDate]);

  /**
   * ---- Calendar creation (Monday-first, Angular logic) ----
   */
  // updated getDisabled method - only disable past dates
  const getDisabled = useCallback((day: Moment) => {
    return day.isBefore(moment(), "day"); // only past dates are disabled
  }, []);

  // rest of your visibility functions stay same
  const getVisibilityHidden = useCallback((day: Moment) => {
    return day.month() + 1 !== selectedMoment.month;
  }, [selectedMoment.month]);

  const getVisibilityNone = useCallback((day: Moment) => {
    return day.month() + 1 > selectedMoment.month;
  }, [selectedMoment.month]);

  const createCalendar = useCallback((
    year: number,
    month: number,
    enabledDates: string[] // array of "YYYY-MM-DD"
  ) => {
    let rows: any[] = [];

    const firstDayOfMonth = moment([year, month - 1]).startOf("month");
    const daysInMonth = firstDayOfMonth.daysInMonth();
    const zeroPrefixCount = (firstDayOfMonth.day() + 6) % 7;

    // ✅ Normalize enabled dates to "YYYY-MM-DD" without time zone differences
    const slotDates = enabledDates.map((d) =>
      moment(d).format("YYYY-MM-DD")
    );

    let currentDay = 1;
    while (currentDay <= daysInMonth) {
      const week = Array(7)
        .fill(0)
        .map((_, index) => {
          if (rows.length === 0 && index < zeroPrefixCount) {
            return 0;
          } else if (currentDay <= daysInMonth) {
            let date = firstDayOfMonth.clone().date(currentDay);
            let dateStr = date.format("YYYY-MM-DD");

            let day = {
              date,
              isDisabled: getDisabled(date), // only past dates are disabled
              hasSlots: slotDates.includes(dateStr), // track if date has slots
              isHidden: getVisibilityHidden(date),
              isNone: getVisibilityNone(date),
            };
            currentDay++;
            return day;
          }
          return 0;
        });

      rows.push({ days: week });
    }

    setCalendarData(rows);
  }, [getDisabled, getVisibilityHidden, getVisibilityNone]);

  /**
   * ---- Month navigation (matches Angular) ----
   */
  const goToNextMonth = useCallback((year: number, month: number) => {
    let y = year;
    let m = month;
    if (m >= 12) {
      y = year + 1;
      m = 1;
    } else {
      m = month + 1;
    }
    const mm = moment()
      .month(m - 1)
      .format("MMMM");
    const next = { year: y, month: m, monthName: mm };
    
    setSelectedMoment(next);
    setSelectedDate(null);
    setSelectedSlot(null);
    setBookingSlotsShiftwise([]);

    const toPersist = { ...readPersisted(), selectedMoment: next };
    writePersisted(toPersist);
  }, []);

  const goToPrevousMonth = useCallback((year: number, month: number) => {
    // (typo preserved to match Angular name)
    let y = year;
    let m = month;
    if (m === 1) {
      y = year - 1;
      m = 12;
    } else {
      m = month - 1;
    }
    const mm = moment()
      .month(m - 1)
      .format("MMMM");
    const prev = { year: y, month: m, monthName: mm };
    
    setSelectedMoment(prev);
    setSelectedDate(null);
    setSelectedSlot(null);
    setBookingSlotsShiftwise([]);

    const toPersist = { ...readPersisted(), selectedMoment: prev };
    writePersisted(toPersist);
  }, []);

  /**
   * ---- Selection handlers (mirror Angular) ----
   */
  const getFormatedDate = useCallback((day?: Moment | string) => {
    if (!day) return "";
    return moment(day).format("YYYY-MM-DD");
  }, []);

  const getSelectDate = useCallback((day: Moment) => {
    setSelectedSlot(null);
    setSelectedDate(day);

    // Match date ignoring time
    const slotsForDate =
      appointmentSlotsData?.bookingSlotsShiftwise?.filter((slot: any) =>
        moment(slot.date).isSame(day, "day")
      ) || [];

    const mergedSlots = [
      ...(slotsForDate[0]?.morningSlots || []),
      ...(slotsForDate[0]?.afternoonSlots || []),
      ...(slotsForDate[0]?.eveningSlots || []),
      ...(slotsForDate[0]?.nightSlots || []),
    ];
    // Update state so UI can show filtered slots
    setBookingSlotsShiftwise(mergedSlots);

    setTimeout(() => scrollToBottom(), 100);
  }, [appointmentSlotsData?.bookingSlotsShiftwise, scrollToBottom]);

  const selectSlot = useCallback((slot: ShiftSlot) => {
    setSelectedSlot(slot);
    setTimeout(() => scrollToBottom(), 100);
  }, [scrollToBottom]);

  const confirmSlot = useCallback(() => {
    console.log(selectedSlot);
  }, [selectedSlot]);

  // Memoize timezone change handler
  const handleTimeZoneChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextTz = e.target.value;
    setSelectedTimeZone(nextTz);
    const persistedNow = {
      ...readPersisted(),
      selectedTimeZone: nextTz,
    };
    writePersisted(persistedNow);
  }, []);

  // Memoize appointment type display
  const appointmentTypeDisplay = useMemo(() => {
    if (!appointmentSlotsData.appointmentTypeName && !appointmentSlotsData.duration) {
      return null;
    }
    return (
      <div className="text-gray-600 text-sm">
        {appointmentSlotsData.appointmentTypeName}{" "}
        {appointmentSlotsData.duration} Min
      </div>
    );
  }, [appointmentSlotsData.appointmentTypeName, appointmentSlotsData.duration]);

  // Memoize no slots message
  const noSlotsMessage = useMemo(() => {
    if (Object.keys(bookingSlotsDatewise).length > 0) return null;
    
    return (
      <div className="text-sm absolute w-[100%] h-[100%] -top-3 left-0 flex item-center justify-center">
        <div className="px-5 py-3 rounded-[24px] shadow-lg bg-white m-auto">
          <div className="mb-1 text-xs">No times in {selectedMoment.monthName}</div>
          <button
            className="font-bold inline-flex items-center gap-1"
            style={{ color: "#2959AF" }}
            onClick={() =>
              goToNextMonth(selectedMoment.year, selectedMoment.month)
            }
          >
            View next month
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
              <path
                d="M0.745532 12C0.54539 12 0.357756 11.9256 0.217655 11.7912C-0.0725518 11.5128 -0.0725518 11.0568 0.217655 10.7784L5.19621 6.0024L0.217655 1.224C-0.0725518 0.9432 -0.0725518 0.4896 0.217655 0.2112C0.360257 0.0744 0.547891 0 0.745532 0C0.945675 0 1.13331 0.0744 1.27341 0.2112L6.78234 5.4936C6.92244 5.628 7 5.808 7 6C7 6.192 6.92244 6.372 6.78234 6.5064L1.27591 11.7912C1.13331 11.9256 0.945675 12 0.745532 12Z"
                fill="#FF8C76"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }, [bookingSlotsDatewise, selectedMoment.monthName, goToNextMonth, selectedMoment.year, selectedMoment.month]);

  // Memoize slots view
  const slotsView = useMemo(() => {
    if (!selectedDate) return null;
    
    return (
      <div className="mb-6 border p-3 rounded-lg">
        <div className="text-sm font-medium mb-2">
          {selectedDate.format("DD")} {selectedDate.format("MMMM")},{" "}
          {selectedMoment.year}
        </div>

        <div className="flex flex-wrap gap-2">
          {bookingSlotsShiftWise?.length > 0 ? (
            bookingSlotsShiftWise.map((slot: any) => {
              const isSel = selectedSlot?.startTime === slot.startTime;
              return (
                <button
                  key={slot.id}
                  onClick={() => selectSlot(slot)}
                  className={`px-3 py-1 rounded border text-sm ${isSel ? "text-white" : ""
                    }`}
                  style={{
                    color: isSel ? "#fff" : calendarColors.main,
                    backgroundColor: isSel ? calendarColors.hover : "#fff",
                    borderColor: calendarColors.main,
                  }}
                >
                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                </button>
              );
            })
          ) : (
            <div className="text-sm text-gray-600">
              No slots available for this date
            </div>
          )}
        </div>
      </div>
    );
  }, [selectedDate, selectedMoment.year, bookingSlotsShiftWise, selectedSlot, selectSlot, calendarColors, formatTime]);

  // Memoize confirm button
  const confirmButton = useMemo(() => {
    if (!selectedSlot || !selectedDate) return null;
    
    return (
      <button
        onClick={confirmSlot}
        className="w-full text-white font-medium py-3 rounded-full mb-10"
        style={{
          backgroundColor: calendarColors.main || "#2959AF",
        }}
      >
        <span className="inline-flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4.70484 1.612L5.48284 1.378C5.90904 1.24948 6.36743 1.28042 6.77249 1.46504C7.17755 1.64966 7.5016 1.97534 7.68418 2.38133L8.28551 3.71866C8.44249 4.06786 8.4862 4.45745 8.41052 4.83275C8.33483 5.20806 8.14355 5.55026 7.86351 5.81133L6.86684 6.74C6.83773 6.76713 6.81928 6.80378 6.81484 6.84333C6.78551 7.108 6.96484 7.62333 7.37884 8.34C7.67884 8.86066 7.95151 9.22666 8.18284 9.43133C8.34484 9.57466 8.43351 9.60533 8.47151 9.59466L9.81151 9.18466C10.1774 9.07273 10.5692 9.07811 10.932 9.20006C11.2947 9.322 11.6102 9.5544 11.8342 9.86466L12.6875 11.0487C12.9473 11.4086 13.0676 11.8507 13.0262 12.2926C12.9848 12.7346 12.7843 13.1466 12.4622 13.452L11.8708 14.012C11.5574 14.3089 11.1723 14.5195 10.7533 14.6234C10.3342 14.7272 9.89535 14.7208 9.47951 14.6047C7.64351 14.092 5.99751 12.5427 4.52284 9.98866C3.04684 7.43133 2.52818 5.22866 3.00684 3.38C3.11429 2.96422 3.32703 2.58313 3.62456 2.27346C3.92208 1.96379 4.29369 1.73598 4.70484 1.612Z"
              fill="white"
            />
          </svg>
          Confirm scheduling
        </span>
      </button>
    );
  }, [selectedSlot, selectedDate, confirmSlot, calendarColors.main]);

  /**
   * ---- UI ----
   * Tailwind-ish classes; feel free to swap for your design system.
   */
  return (
    <div
      id="book-appointment"
      ref={containerRef}
      className="h-full overflow-auto relative p-4 mx-auto"
    >
      <div className="w-[50%] mx-auto">

        {/* Heading */}
        <div className="mb-4">
          <div className="text-gray-900 text-lg font-semibold">
            Select a Date & Time
          </div>
          {appointmentTypeDisplay}
        </div>

        {/* Time Zone Select */}
        <div className="mb-4">
          <select
            className="w-full border rounded p-2"
            value={selectedTimeZone}
            onChange={handleTimeZoneChange}
          >
            <option value="" disabled>
              Choose…
            </option>
            {availableTimeZonesList.map((tz) => (
              <option key={tz.id} value={tz.id}>
                {tz.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Calendar Box */}
        <div className="relative border rounded-lg overflow-hidden mb-6 bg-gray-100">
          {
            !isLoader ?
              <>
                {/* Header Row */}
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="font-medium">
                    {moment()
                      .month(selectedMoment.month - 1)
                      .format("MMMM")}{" "}
                    {selectedMoment.year}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Prev */}
                    <button
                      onClick={() =>
                        goToPrevousMonth(selectedMoment.year, selectedMoment.month)
                      }
                      className={`p-2 rounded ${moment().year() === selectedMoment.year &&
                        moment().month() + 1 === selectedMoment.month
                        ? "opacity-40 pointer-events-none"
                        : "hover:bg-gray-100"
                        }`}
                      aria-label="Previous month"
                    >
                      <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
                        <path
                          d="M6.25447 0C6.45461 0 6.64224 0.0744 6.78234 0.2088C7.07255 0.4872 7.07255 0.9432 6.78234 1.2216L1.80379 5.9976L6.78234 10.776C7.07255 11.0568 7.07255 11.5104 6.78234 11.7888C6.63974 11.9256 6.45211 12 6.25447 12C6.05432 12 5.86669 11.9256 5.72659 11.7888L0.217655 6.5064C0.0775552 6.372 0 6.192 0 6C0 5.808 0.0775551 5.628 0.217655 5.4936L5.72409 0.2088C5.86669 0.0744 6.05432 0 6.25447 0Z"
                          fill="#FF8C76"
                        />
                      </svg>
                    </button>
                    {/* Next */}
                    <button
                      onClick={() =>
                        goToNextMonth(selectedMoment.year, selectedMoment.month)
                      }
                      className="p-2 rounded hover:bg-gray-100"
                      aria-label="Next month"
                    >
                      <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
                        <path
                          d="M0.745532 12C0.54539 12 0.357756 11.9256 0.217655 11.7912C-0.0725518 11.5128 -0.0725518 11.0568 0.217655 10.7784L5.19621 6.0024L0.217655 1.224C-0.0725518 0.9432 -0.0725518 0.4896 0.217655 0.2112C0.360257 0.0744 0.547891 0 0.745532 0C0.945675 0 1.13331 0.0744 1.27341 0.2112L6.78234 5.4936C6.92244 5.628 7 5.808 7 6C7 6.192 6.92244 6.372 6.78234 6.5064L1.27591 11.7912C1.13331 11.9256 0.945675 12 0.745532 12Z"
                          fill="#FF8C76"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Days Header (Monday..Sunday) */}
                <div className="grid grid-cols-7 text-center text-xs font-medium text-gray-600 py-2">
                  {nameOfDays.map((d) => (
                    <div key={d.code}>{d.name.slice(0, 3)}</div>
                  ))}
                </div>

                {/* Date Grid */}
                <div className="px-2 pb-3 relative">
                  {calendarData.map((row: any, ri: any) => (
                    <div key={ri} className="grid grid-cols-7 gap-1 mb-1">
                      {row.days.map((cell: any, ci: any) => {
                        if (cell === 0) {
                          return <div key={ci} />;
                        }
                        const dateStr = getFormatedDate((cell as any).date);
                        const hasSlots = (cell as any).hasSlots;
                        const isSelected =
                          selectedDate &&
                          (cell as any).date.isSame(selectedDate, "day");

                        const style: React.CSSProperties = {
                          color: isSelected
                            ? "#fff"
                            : hasSlots
                              ? (calendarColors.main as string)
                              : "black",
                          backgroundColor: isSelected
                            ? (calendarColors.hover as string)
                            : hasSlots
                              ? "#2959AF14"
                              : "#f3f4f6", // light gray background for dates without slots
                          borderColor: hasSlots ? calendarColors.main : "#d1d5db", // different border for dates without slots
                        };

                        const classes = [
                          "h-10 flex items-center justify-center rounded cursor-pointer border",
                          (cell as any).isHidden ? "invisible" : "",
                          (cell as any).isDisabled
                            ? "opacity-40 pointer-events-none"
                            : hasSlots
                              ? "hover:bg-gray-100"
                              : "opacity-60", // slightly dimmed for dates without slots
                          isSelected ? "ring-2 ring-offset-1" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <div
                            key={ci}
                            className={classes}
                            style={style}
                            onClick={() => !(cell as any).isDisabled && getSelectDate((cell as any).date)}
                            title={`${moment((cell as any).date).format("ddd, MMM D")}${hasSlots ? " - Slots available" : " - No slots"}`}
                          >
                            <div className="text-sm">{(cell as any).date.date()}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {/* Calendar Legend */}
                  <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-600">
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded border" style={{ backgroundColor: "#2959AF14", borderColor: calendarColors.main }}></div>
                        <span>Available slots</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded border" style={{ backgroundColor: "#f3f4f6", borderColor: "#d1d5db" }}></div>
                        <span>No slots</span>
                      </div>
                    </div>
                  </div>

                  {/* No Slots Box (when month has none) */}
                  {noSlotsMessage}
                </div>

              </>
              : <div className="flex items-center justify-center min-h-[312px]">
                <CircularProgress />
              </div>
          }

        </div>

        {/* Slots view for selected date */}
        {slotsView}

        {/* Confirm Button */}
        {confirmButton}

      </div>

    </div>
  );
};

export default ScheduleCall;